import { useState } from 'react';
import type { WorkoutInput } from '@st/contract';
import { useTranslation } from '../../../i18n/I18nProvider';
import { WORKOUT_TYPES } from '../../../lib/program';
import { Button, Input, Modal, NumberInput, Select } from '../../../ui';
import { WORKOUT_TYPE_KEY } from './categories';

export interface WorkoutFormModalProps {
  open: boolean;
  dayLabel: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: Omit<WorkoutInput, 'dayIndex'>) => void;
}

/** Stvaranje treninga u točno određenom danu — dan je već odabran klikom. */
export function WorkoutFormModal({
  open,
  dayLabel,
  pending,
  onClose,
  onSubmit,
}: WorkoutFormModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [type, setType] = useState<WorkoutInput['type']>('strength');
  const [minutes, setMinutes] = useState<number | ''>(45);
  const [error, setError] = useState<string | undefined>(undefined);

  function submit() {
    if (name.trim().length === 0) {
      setError(t('builder.nameRequired'));
      return;
    }
    setError(undefined);
    onSubmit({
      nameHr: name.trim(),
      type,
      estimatedMinutes: minutes === '' ? null : minutes,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${t('builder.newWorkout')} — ${dayLabel}`}
      closeLabel={t('a11y.closeDialog')}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" loading={pending} onClick={submit}>
            {t('action.add')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          id="new-workout-name"
          label={t('builder.workoutName')}
          value={name}
          required
          error={error}
          onChange={(e) => setName(e.target.value)}
        />
        <Select
          id="new-workout-type"
          label={t('builder.workoutType')}
          value={type}
          onChange={(e) => setType(e.target.value as WorkoutInput['type'])}
          options={WORKOUT_TYPES.map((v) => ({ value: v, label: t(WORKOUT_TYPE_KEY[v] ?? v) }))}
        />
        <NumberInput
          id="new-workout-minutes"
          label={t('builder.estimatedMinutes')}
          value={minutes}
          min={1}
          max={300}
          onValueChange={setMinutes}
        />
      </form>
    </Modal>
  );
}
